/**
 * Module dependencies.
 */

import { Editor, useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Bold, Code, Heading1, Heading2, Highlighter, Italic, Link2, Strikethrough } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ToolButton, ToolDivider } from './tool-button';

/**
 * `Props` type.
 */

type Props = {
  editor: Editor;
  onLink: () => void;
};

/**
 * Export `EditorBubbleMenu` component — inline formatting shown over a text
 * selection.
 */

export function EditorBubbleMenu({ editor, onLink }: Props): JSX.Element {
  const [translate] = useTranslation();
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current.isActive('bold'),
      code: current.isActive('code'),
      h1: current.isActive('heading', { level: 1 }),
      h2: current.isActive('heading', { level: 2 }),
      highlight: current.isActive('highlight'),
      italic: current.isActive('italic'),
      link: current.isActive('link'),
      strike: current.isActive('strike')
    })
  });

  const label = (key: string) => translate(`notes.editor.${key}`);

  return (
    <BubbleMenu
      className={
        'flex items-center gap-0.5 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg'
      }
      editor={editor}
      options={{ offset: 8, placement: 'top' }}
      shouldShow={({ editor: current, state: editorState }) => {
        const { selection } = editorState;

        return (
          !selection.empty &&
          !('node' in selection) &&
          !current.isActive('codeBlock') &&
          !current.isActive('image') &&
          current.isEditable
        );
      }}
    >
      <ToolButton
        active={state.h1}
        label={label('blocks.heading1')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 />
      </ToolButton>

      <ToolButton
        active={state.h2}
        label={label('blocks.heading2')}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 />
      </ToolButton>

      <ToolDivider />

      <ToolButton
        active={state.bold}
        label={label('marks.bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold />
      </ToolButton>

      <ToolButton
        active={state.italic}
        label={label('marks.italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic />
      </ToolButton>

      <ToolButton
        active={state.strike}
        label={label('marks.strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough />
      </ToolButton>

      <ToolButton
        active={state.code}
        label={label('marks.code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code />
      </ToolButton>

      <ToolButton
        active={state.highlight}
        label={label('marks.highlight')}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter />
      </ToolButton>

      <ToolDivider />

      <ToolButton active={state.link} label={label('marks.link')} onClick={onLink}>
        <Link2 />
      </ToolButton>
    </BubbleMenu>
  );
}
